import { getMostRecurringCausesAtWork, getMostRecurringCausesInCategory } from "./MostRecurringCausesHelper";

// One story can have max 8 causes shown on screens taller than 760px, else 5 max on smaller screens.
const maxChildCount = window?.visualViewport.height >= 760 ? 8 : 5;

/**
 * @param {string} mappedComponent - The component key string that's responsible for displaying this info 
 * @returns {Array} An array of story objects e.g. [{component: 'abc', 
 * sections: Array of objects representing the styled elements to draw, 
 * isUplifting: boolean,
 * heading: string
 * }]
 */
export function getUplifterDepleterStories(currentWeekFlows, mappedComponent) {
    let stories = [];

    const mostRecurringUpliftersWork = getMostRecurringCausesAtWork(currentWeekFlows, 'pleasant') || [];
    const mostRecurringUpliftersLife = getMostRecurringCausesInCategory(currentWeekFlows, 'pleasant', 'causes_beyond_work') || [];
    const uplftingStories = getOrderedWorkLifeStories(mostRecurringUpliftersWork, mostRecurringUpliftersLife, true)
        .map((story, i) => ({
            component: mappedComponent,
            data: {
                isUplifting: true,
                heading: story.heading,
                sections: story.sections
            },
            duration: maxChildCount > 5 ? (story.spaceLeft < 3 ? 7500 : 6000) : (story.spaceLeft <= 1 ? 6500 : 5000),
        }));
    stories = stories.concat(uplftingStories);

    const mostRecurringDepletersWork = getMostRecurringCausesAtWork(currentWeekFlows, 'unpleasant') || [];
    const mostRecurringDepletersLife = getMostRecurringCausesInCategory(currentWeekFlows, 'unpleasant', 'causes_beyond_work') || [];
    const depletingStories = getOrderedWorkLifeStories(mostRecurringDepletersWork, mostRecurringDepletersLife, false)
        .map((story, i) => ({
            component: mappedComponent,
            data: {
                isUplifting: false,
                heading: story.heading,
                sections: story.sections
            },
            duration: maxChildCount > 5 ? (story.spaceLeft < 3 ? 7500 : 6000) : (story.spaceLeft <= 1 ? 6500 : 5000),
        }));
    stories = stories.concat(depletingStories);

    stories.forEach((causeStory, i) => {
        causeStory.index = i;
    });

    return stories;
}

/**
 * @param {Array} mostRecurringInWork Uplifters OR depleters
 * @param {Array} mostRecurringInLife Uplifters OR depleters
 * @param {boolean} isUplifting true if the stories being organized are for uplifting causes. 
 * @returns {Array} An array of story objects in the order they should be shown.
 */
function getOrderedWorkLifeStories(recurringWorkAnswers, recurringLifeAnswers, isUplifting) {
    const keyword = isUplifting ? 'uplifters' : 'depleters';
    const workStoriesAndInfo = getWorkStories(recurringWorkAnswers, isUplifting);
    let stories = workStoriesAndInfo?.stories ?? [];

    let lifeSection;
    if (recurringLifeAnswers?.length > 0) {
        lifeSection = {
            transparentTag: `In Life`,
            childCount: recurringLifeAnswers.length + 1, // 1 added coz "In Life" heading also occupies a tag space
            highlightedAnswers: recurringLifeAnswers.map(obj => [obj.cause, obj.count])
        };
    }

    // figure out if life is added to an existing story or is its own thing
    let isLifeCombinedWithWork = false;
    if (lifeSection) {
        const standaloneLifeStory = { sections: [lifeSection], heading: `Top ${keyword} in Life` };
        if (!workStoriesAndInfo) {
            lifeSection.transparentTag = null;
            return [standaloneLifeStory];
        }

        let lastStory = stories[stories.length - 1];
        // if no room for life section in last story
        if (lifeSection.childCount > lastStory.spaceLeft) {
            lifeSection.transparentTag = null; // we don't need this transparent heading when life is standalone.
            stories.push(standaloneLifeStory);
        }
        else {
            isLifeCombinedWithWork = true;
            lastStory.sections.push(lifeSection);
            lastStory.spaceLeft -= lifeSection.childCount;
            // Update the heading for the first work story because now life is combined with the uplifters
            stories[0].heading = `Most recurring ${keyword} this week`;
        }
    }

    // check if the work story had an extra heading tag that needs to be removed
    if (workStoriesAndInfo?.hasNestedAnswers === false && !isLifeCombinedWithWork) {
        stories[0].sections[0].transparentTag = null;
    }

    return stories;
}

function getWorkStories(recurringWorkAnswers, isUplifting) {
    if (recurringWorkAnswers.length === 0) {
        return null;
    }

    let workSections = [];
    let hasNestedAnswers = false;
    for (let item of recurringWorkAnswers) {
        const causeNameAndCount = `${item.cause} ${item.count}`;
        let section = { num: workSections.length + 1, childCount: 1, transparentTag: causeNameAndCount };
        if (item.topRecurringAnswers) {
            hasNestedAnswers = true;
            section.highlightedAnswers = item.topRecurringAnswers;
            section.childCount += item.topRecurringAnswers.length;
        }
        workSections.push(section);
    }

    // if there were no nested answers, then the top level causes will be shown together in 1 section
    if (!hasNestedAnswers) {
        workSections = [
            {
                childCount: recurringWorkAnswers.length,
                transparentTag: `In Work`,
                // // whiteTags accepts an array of the strings that to show as individual answers on white bg
                // whiteTags: recurringWorkAnswers.map(item => `${item.cause} ${item.count}`),
                // To show highlighted, it needs to an array of tuples where each has the answer and count shown on color
                highlightedAnswers: recurringWorkAnswers.map(item => [item.cause, item.count]),
            }
        ];
    }

    // Put sections into stories.
    const heading = isUplifting ? `Top recurring uplifters at Work` : `Most frequent depleters at work were...`;
    let stories = [{ spaceLeft: maxChildCount, sections: [], heading: heading }];
    workSections.forEach(section => {
        const lastStory = stories[stories.length - 1];
        const spaceLeftInLastStory = lastStory.spaceLeft ?? maxChildCount;
        if (spaceLeftInLastStory >= section.childCount) {
            lastStory.sections.push(section);
            lastStory.spaceLeft -= section.childCount;
        }
        else {
            // put section in next story. 
            stories.push({ spaceLeft: maxChildCount - section.childCount, sections: [section] });
            // What about sections that have more than 8 things? Do we need to split them up? 
            // TODO: No, a section shouldn't be allowed to have more than 8 things ever. Too much info
        }
    })

    return { stories, hasNestedAnswers };
}